<template>
  <b-overlay :show="loading" class="animated">
    <modal-trash-basket
      v-if="show_modal"
      :entity_type="entity_type"
      :trash="trash"
      @onUpdate="onUpdate"
    />
    <v-server-table
      :columns="table_config.columns"
      :options="table_config.options"
      :theme="theme"
      :ref="ref"
      id="submission-server-table"
      class="w-100"
    >
      <div slot="beforeTable" class="pt-3">
        <b-row>
          <b-col>
            <select-filter
              variant="info"
              icon="fa-toggle-on"
              placeholder="Quality Control"
              :list="[
                { value: '1', text: 'Yes - Quality Control' },
                { value: '0', text: 'No - Quality Control' },
              ]"
              :selected="show_quality_control"
              filter-field="quality_control"
              filter-operator="eq"
              @onSelect="setCustomFilter('quality_control', $event)"
            />
          </b-col>

          <b-col>
            <select-filter
              variant="info"
              :icon="sample_type.icon"
              placeholder="All Sample Types"
              :collection="sample_type.filters"
              value-key="id"
              text-key="name"
              filter-field="sample_type_id"
              filter-operator="eq"
              @onSelect="setCustomFilter('sample_type', $event); fetchAnalytes(); fetchTestPackages()"
            />
          </b-col>
          <b-col class="px-0">
            <select-filter
              variant="info"
              :icon="analyte.icon"
              placeholder="All Analytes"
              :collection="analyte.filters"
              value-key="id"
              text-key="name"
              filter-field="test_package.test_package_samples.test_package_analytes.analyte_id"
              filter-operator="eq"
              :disabled="!customFilters.sample_type"
              @onSelect="setCustomFilter('analyte', $event)"
            />
          </b-col>
          <b-col>
            <select-filter
              variant="info"
              :icon="test_package.icon"
              placeholder="All Test Packages"
              :collection="test_package.filters"
              value-key="id"
              text-key="name"
              filter-field="test_package_id"
              filter-operator="eq"
              :disabled="!customFilters.sample_type"
              @onSelect="setCustomFilter('test_package', $event)"
            />
          </b-col>
        </b-row>
      </div>
      <span slot="pid" slot-scope="{ row }">
        <router-link :to="`/submissions/${row.id}`">
          <b>{{ row.publicNumber() }}</b>
        </router-link>
      </span>
      <span slot="project.pid" slot-scope="{ row }">
        <existence-gate :entity="row" :levels="['project']" :field="'pid'">
          <template #existing-content>
            <router-link
              :to="`/projects/${getData(row, {
                levels: ['project'],
                field: 'id',
              })}`"
            >
              {{ row.getProject().publicNumber() }}
            </router-link>
          </template>
        </existence-gate>
      </span>
      <span slot="project.customer.full_name" slot-scope="{ row }">
        <existence-gate
          :entity="row"
          :levels="table_data.customer"
          :field="'full_name'"
        >
          <template #existing-content>
            <router-link
              :to="`/customer/${getData(row, {
                levels: table_data.customer,
                field: 'id',
              })}`"
            >
              {{ getData(row, { levels: table_data.customer, field: "full_name" }) }}
            </router-link>
          </template>
        </existence-gate>
      </span>
      <span slot="turnaround.name" slot-scope="{ row }">
        <existence-gate :entity="row" :levels="['turnaround']" :field="'name'">
          <template #existing-content>
            <router-link
              :to="`/turnarounds/${getData(row, {
                levels: ['turnaround'],
                field: 'id',
              })}`"
            >
              {{ getData(row, { levels: ["turnaround"], field: "name" }) }}
            </router-link>
          </template>
        </existence-gate>
      </span>
      <span slot="sample_type.name" slot-scope="{ row }">
        <existence-gate :entity="row" :levels="['sample_type']" :field="'name'">
          <template #existing-content>
            <router-link
              :to="`/sampletypes/${getData(row, {
                levels: ['sample_type'],
                field: 'id',
              })}`"
            >
              {{ getData(row, { levels: ["sample_type"], field: "name" }) }}
            </router-link>
          </template>
        </existence-gate>
      </span>
      <span slot="test_package.name" slot-scope="{ row }">
        <existence-gate
          :entity="row"
          :levels="['test_package']"
          :field="'name'"
        >
          <template #existing-content>
            <router-link
              :to="`/testpackages/${getData(row, {
                levels: ['test_package'],
                field: 'id',
              })}`"
            >
              {{ getData(row, { levels: ["test_package"], field: "name" }) }}
            </router-link>
          </template>
        </existence-gate>
      </span>
      <span slot="sample_count" slot-scope="{ row }">
        {{ row.getSamples().models.length }}
      </span>
      <span slot="created_at" slot-scope="{ row }">
        {{ row.created_at | moment("L") }}
      </span>
      <span slot="quality_control" slot-scope="{ row }">
        {{ row.quality_control ? 'Yes' : 'No' }}
      </span>
      <span slot="received_percent" slot-scope="{ row }">
        <submission-progress-badge :submission="row" status_name="Received" />
      </span>
      <span slot="prepped_percent" slot-scope="{ row }">
        <submission-progress-badge :submission="row" status_name="Prepped" />
      </span>
      <span slot="scheduled_percent" slot-scope="{ row }">
        <submission-progress-badge :submission="row" status_name="Scheduled" />
      </span>
      <span slot="results_percent" slot-scope="{ row }">
        <submission-progress-badge :submission="row" status_name="Complete" />
      </span>
      <span slot="attribute_1" slot-scope="{ row }">
        <p v-b-tooltip.hover :title="getAttributeHover(row, 'attribute_1')">
          {{ row.attribute_1 }}
        </p>
      </span>
      <span slot="attribute_2" slot-scope="{ row }">
        <p v-b-tooltip.hover :title="getAttributeHover(row, 'attribute_2')">
          {{ row.attribute_2 }}
        </p>
      </span>
      <span slot="attribute_3" slot-scope="{ row }">
        <p v-b-tooltip.hover :title="getAttributeHover(row, 'attribute_3')">
          {{ row.attribute_3 }}
        </p>
      </span>
      <span slot="actions" slot-scope="{ row }">
        <router-link
          v-show="checkPermission('edit submission')"
          class="btn btn-warning px-1 py-0 text-white mr-1"
          :to="`/submissions/${row.id}/edit`"
        >
          <i class="fa fa-pencil" />
        </router-link>
        <template v-if="checkPermission('delete submission')">
          <a
            v-if="isDeleted(row.id)"
            class="btn btn-success px-1 py-0 text-white mr-1"
            @click="restoreFromTrash(row.id)"
          >
            <i class="fa fa-refresh" />
          </a>
          <a
            v-else
            class="btn btn-danger px-1 py-0 text-white mr-1"
            @click="putToTrash(row)"
          >
            <i class="fa fa-trash" />
          </a>
        </template>
      </span>
    </v-server-table>
  </b-overlay>
</template>

<script>
import Vue from "vue";
import SubmissionProgressBadge from "./SubmissionProgressBadge";
import BaseServerTable from "@/components/shared/BaseServerTable";
import ExistenceGate from "@/components/shared/ExistenceGate";
import ModalTrashBasket from "@/components/shared/ModalTrashBasket";
import SelectFilter from "@/components/shared/SelectFilter";
import SubmissionCollection from "@/collections/lab/SubmissionCollection";
import AnalyteModel from "@/models/lab/AnalyteModel";
import SampleTypeModel from "@/models/lab/SampleTypeModel";
import TestPackageModel from "@/models/lab/TestPackageModel";
import { mapActions, mapGetters } from "vuex";
import { ServerTable } from "vue-tables-2";
import { AccessControlCheck } from "@/auth/AccessControlCheck";
import { getCollection } from "@/shared/fetch-utils";
import { getEntityData } from "@/shared/utils";

import SubmissionExtensionTableDTO from '@/DTO/ExtensionTables/SubmissionExtensionTableDTO';

Vue.use(ServerTable);

export default {
  name: "SubmissionServerTable",
  extends: BaseServerTable,
  components: {
    ExistenceGate,
    ModalTrashBasket,
    SubmissionProgressBadge,
    ServerTable,
    SelectFilter,
  },
  props: {
    collection: {
      type: Object,
      default: () => new SubmissionCollection(),
    },
    analytes: {
      type: Object,
      default: () => null,
    },
    sample_types: {
      type: Object,
      default: () => null,
    },
    test_packages: {
      type: Object,
      default: () => null,
    },
    show_columns: {
      type: Array,
      default: () => [
        "pid",
        "project.pid",
        "event_code",
        "project.customer.full_name",
        "turnaround.name",
        "sample_type.name",
        "test_package.name",
        "sample_count",
        "created_at",
        "quality_control",
        "received_percent",
        "prepped_percent",
        "scheduled_percent",
        "results_percent",
        "attribute_1",
        "attribute_2",
        "attribute_3",
        "actions",
      ],
    },
    filterable: {
      type: Array,
      default: () => ["searchable"],
    },
    sortable: {
      type: Array,
      default: () => [
        "pid",
        "project.pid",
        "event_code",
        "project.customer.full_name",
        "turnaround.name",
        "sample_type.name",
        "test_package.name",
        "created_at",
      ],
    },
  },
  data() {
    const actions_access =
      this.checkPermission("edit submission") ||
      this.checkPermission("delete submission");

    return {
      entity_type: "submission",
      project_id: this.$route.params.project_id || null,
      test_package_id: this.$route.params.test_package_id || null,
      turnaround_id: this.$route.params.turnaround_id || null,
      analyte_collection: null,
      sample_type_collection: null,
      test_package_collection: null,
      table_config: Object.assign(this.getDefaultTableConfig(actions_access), {
        options: {
          headings: {
            pid: "#",
            "project.pid": "Project",
            event_code: "Code",
            "project.customer.full_name": "Customer",
            "turnaround.name": "Turnaround",
            "sample_type.name": "Type",
            "test_package.name": "Package",
            sample_count: "Samples",
            created_at: "Submitted",
            quality_control: "QC",
            received_percent: "Received",
            prepped_percent: "Prepped",
            scheduled_percent: "Scheduled",
            results_percent: "Results",
            attribute_1: "Attribute 1",
            attribute_2: "Attribute 2",
            attribute_3: "Attribute 3",
          },
        },
      }),
      table_data: {
        customer: ["project", "customer"],
        status: ["current_submission_status_log", "submission_status"],
      },
      show_quality_control: 0
    };
  },
  computed: {
    ...mapGetters({
      trash: "submissionTrash",
    }),
    analyte() {
      return {
        icon: `fa-${AnalyteModel.iconStatic()}`,
        filters: this.analytes || this.analyte_collection,
      };
    },
    sample_type() {
      return {
        icon: `fa-${SampleTypeModel.iconStatic()}`,
        filters: this.sample_types || this.sample_type_collection,
      };
    },
    test_package() {
      return {
        icon: `fa-${TestPackageModel.iconStatic()}`,
        filters: this.test_packages || this.test_package_collection,
      };
    },
    show_modal() {
      return (
        this.checkPermission("delete submission") &&
        this.show_columns.includes("actions")
      );
    },
  },
  methods: {
    ...mapActions(["PUT_TO_TRASH", "RESTORE_FROM_TRASH"]),
    getData({ _reference }, data) {
      return getEntityData({
        entity: _reference,
        ...data,
      });
    },
    getAttributeHover(submission, attributeNumber) {
      return SubmissionExtensionTableDTO.getHoverTextStatic(submission, attributeNumber);
    },
    checkPermission(permission) {
      return AccessControlCheck.checkPermission(permission);
    },
    putToTrash(value) {
      this.PUT_TO_TRASH({ value, name: this.entity_type });
    },
    restoreFromTrash(id) {
      this.RESTORE_FROM_TRASH({ id, name: this.entity_type });
    },
    isDeleted(id) {
      return this.trash.find((item) => item.id === id);
    },
    onUpdate() {
      this.$refs[this.ref].refresh();
    },
    async init() {

      const payload = {
        sort: [
          {
            logic: "sortAddAsc",
            field: "name",
          },
        ],
        limit: "all",
        message: this.$bvToast,
      };

      !this.sample_types &&
        (this.sample_type_collection = await getCollection({
          ...payload,
          collection_type: "sample_type",
        }));
    },
    async fetchAnalytes() {
      this.setCustomFilter('analyte', null)
      if (this.analytes) return;
      this.analyte_collection = null

      let filters = []

      if (this.customFilters.sample_type) {
        filters.push({
          field: "sample_type_id",
          logic: "filterEquals",
          value: this.customFilters.sample_type.value
        })
      }

      this.analyte_collection = await getCollection({
        sort: [
          {
            logic: "sortAddAsc",
            field: "name",
          },
        ],
        filters,
        limit: "all",
        message: this.$bvToast,
        collection_type: "analyte",
      });
    },
    async fetchTestPackages() {
      this.setCustomFilter('test_package', null)
      if (this.test_packages) return
      this.test_package_collection = null

      let filters = []

      if (this.customFilters.sample_type) {
        filters.push({
          logic: "filterEquals",
          field: "sample_type_id",
          value: this.customFilters.sample_type.value,
        })
      }

      this.test_package_collection = await getCollection({
        sort: [
          {
            logic: "sortAddAsc",
            field: "name",
          },
        ],
        filters,
        limit: "all",
        message: this.$bvToast,
        collection_type: "test_package",
      })
    },
    prepareCollectionFilter(filter) {
      filter.sortAddDesc("pid", true);
      this.project_id && filter.filterEquals("project_id", this.project_id);
      this.test_package_id &&
        filter.filterEquals("test_package_id", this.test_package_id);
      this.turnaround_id &&
        filter.filterEquals("turnaround_id", this.turnaround_id);
      
      filter.filterEquals("project.laboratory_id", this.$store.getters.getLaboratoryContext.id);
      
      return filter;
    },
    setCustomFilter(key, filter) {
      if (filter && filter.value && filter.value !== "") {
        if (key === 'sample_type') {
          this.setSampleFilter(filter);
        }
        this.customFilters[key] = filter;
      } else if (this.customFilters[key]) {
        delete this.customFilters[key];
      }
      this.$refs[this.ref].refresh();
    },
    async setSampleFilter(event) {
      const payload = {
        sort: [
          {
            logic: "sortAddAsc",
            field: "name",
          },
        ],
        filters: [
          {
            field: event.field,
            logic: "filterEquals",
            value: event.value
          }
        ],
        limit: "all",
        message: this.$bvToast,
      }
      
      this.analyte_collection = await getCollection({
        ...payload,
        collection_type: "analyte",
      });
      
      this.test_package_collection = await getCollection({
        ...payload,
        collection_type: "test_package",
      });
    }
  },
  created() {
    this.init();
    this.toggleColumn("quality_control", this.show_quality_control);
  },
};
</script>
